/**
 * Main App Component
 *
 * - React Router setup
 * - Authentication initialization
 * - Dark mode support (next-themes)
 * - Toast notifications
 * - Global layout
 * - Code Splitting with React.lazy() for optimal bundle size
 */

import { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuthStore } from "./stores/authStore";
import { UserRole } from "./types/auth";
import { ThemeProvider } from "./components/theme-provider";
import { ClinicProvider } from "./contexts/ClinicContext";
import { NavigationGuardProvider } from "./contexts/NavigationGuardContext";
import { ModalStackProvider } from "./contexts/ModalStackContext"; // V199: 동적 모달 z-index 관리
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { CallNotificationPanel } from "./components/notifications/CallNotificationPanel";
import ProtectedRoute from "./components/common/ProtectedRoute";
import OrgAdminRoute from "./components/common/OrgAdminRoute";
import SettingsAccessRoute from "./components/common/SettingsAccessRoute";
import { ScrollToTop } from "./components/common/ScrollToTop";
import { ErrorBoundaryWithReset } from "./components/common/ErrorBoundary";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import ReservationLayout from "./components/layout/ReservationLayout";

import ReservationListPage from "./pages/appointments/ReservationListPage";

// V1095: Lazy load - AppointmentTimetablePage (카탈로그 기반 타임테이블)
const AppointmentTimetablePage = lazy(
  () => import("./pages/appointments/timetable/AppointmentTimetablePage"),
);

// V1096: Lazy load - BookingDetailPageV2 (예약 상세 페이지 V2)
const BookingDetailPageV2 = lazy(
  () => import("./pages/appointments/BookingDetailPageV2"),
);

// V179: BookingProductsPage는 ReservationSettingsPage에서 lazy import됨
// (이 파일에서는 리다이렉트만 사용하므로 import 불필요)

/**
 * Loading Fallback Component
 * Displayed while lazy-loaded components are loading
 */
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">로딩 중...</p>
      </div>
    </div>
  );
}

/**
 * Lazy-loaded Page Components
 *
 * Benefits:
 * - Reduces initial bundle size by 60-80%
 * - Improves initial page load time
 * - Better caching (chunks loaded on-demand)
 * - Automatic code splitting by route
 */

// Public Pages (Landing, Auth, Legal) - Critical, load immediately
const LandingPage = lazy(() => import("./pages/landing/LandingPage"));
const AuthPage = lazy(() => import("./pages/auth/AuthPage"));
const AdminLoginPage = lazy(() => import("./pages/auth/AdminLoginPage"));

// Legal Pages (Public)
const TermsOfServicePage = lazy(
  () => import("./pages/legal/TermsOfServicePage"),
);
const PrivacyPolicyPage = lazy(() => import("./pages/legal/PrivacyPolicyPage"));

// Core Pages (Dashboard, Change Password) - High priority
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const ChangePasswordPage = lazy(
  () => import("./pages/auth/ChangePasswordPage"),
);

// Feature Pages - Load on demand
const ConsultListPage = lazy(() => import("./pages/consults/ConsultListPage"));
const ConsultDetailPage = lazy(
  () => import("./pages/consults/ConsultDetailPage"),
);
// V1257: AI 학습함 (작업 #4)
const LearningInboxPage = lazy(
  () => import("./pages/learning-inbox/LearningInboxPage"),
);
import CustomerListPage from "./pages/customers/CustomerListPage";
// const CustomerListPage = lazy(() => import('./pages/customers/CustomerListPage'));
const CustomerDetailPage = lazy(
  () => import("./pages/customers/CustomerDetailPage"),
);
// AppointmentsPage 삭제됨 — ReservationListPage로 대체
const OutboundQueuePage = lazy(
  () => import("./pages/outbound/OutboundQueuePage"),
);

// Settings & Subscription - Lower priority
const GeneralSettingsPage = lazy(
  () => import("./pages/settings/GeneralSettingsPage"),
);
const OperationSettingsPage = lazy(
  () => import("./pages/settings/OperationSettingsPage"),
);
// ServiceSettingsPage removed - merged into BookingProductsPage
const ReservationSettingsPage = lazy(
  () => import("./pages/settings/ReservationSettingsPage"),
); // New unified page
// V1080: CustomerGuidePage removed — KB(지식 관리)로 대체
const SettingsChangeHistoryPage = lazy(
  () => import("./pages/settings/SettingsChangeHistoryPage"),
); // V198
const AIAnalysisSettingsPage = lazy(
  () => import("./pages/settings/AIAnalysisSettingsPage"),
); // V1044: AI 분석 설정
const KnowledgeBasePage = lazy(
  () => import("./pages/settings/KnowledgeBasePage"),
); // Knowledge Base 관리
const ConsultationModePage = lazy(
  () => import("./pages/settings/ConsultationModePage"),
); // V1153: 상담 모드 설정
const SubscriptionPage = lazy(
  () => import("./pages/subscription/SubscriptionPage"),
);

// Admin Pages - Admin-only, lazy load
const UserManagementPage = lazy(
  () => import("./pages/users/UserManagementPage"),
);
const PlatformUserManagementPage = lazy(
  () => import("./pages/admin/PlatformUserManagementPage"),
);
const AdminClinicsPage = lazy(() => import("./pages/admin/AdminClinicsPage"));
const StatsPage = lazy(() => import("./pages/stats/StatsPage"));

// Communication Pages (Announcements, FAQ, Inquiries)
const AnnouncementsPage = lazy(
  () => import("./pages/announcements/AnnouncementsPage"),
);
const FaqPage = lazy(() => import("./pages/faq/FaqPage"));
const InquiriesPage = lazy(() => import("./pages/inquiries/InquiriesPage"));
const InquiryDetailPage = lazy(
  () => import("./pages/inquiries/InquiryDetailPage"),
);

// Error Pages
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

/**
 * React Query Client Configuration
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * AppContent Component - Contains routes and auth initialization
 */
function AppContent() {
  const initialize = useAuthStore((state) => state.initialize);

  /**
   * Initialize authentication state on app startup
   */
  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <ErrorBoundaryWithReset>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Routes - Landing Page */}
          <Route path="/main" element={<LandingPage />} />

          {/* Public Routes - Auth Page (Login + Signup) */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />

          {/* Admin Login Route (V108) */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          {/* Legal Pages (Public) */}
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          {/* Change Password Route (Protected) */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes with DashboardLayout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Consult Routes */}
          <Route
            path="/consults"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConsultListPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/consults/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConsultDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* V1257: AI 학습함 (작업 #4) — ORG_POWER+ */}
          <Route
            path="/learning-inbox"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <LearningInboxPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Customer Routes */}
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CustomerListPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CustomerDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* V1096: 예약 상세 V2 (UUID 기반, 독립 페이지) */}
          <Route
            path="/appointments/detail/:uuid"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BookingDetailPageV2 />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Appointments Route (New Naver Place Style) */}
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ReservationLayout />
                </DashboardLayout>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<ReservationListPage />} />
            {/* Legacy redirect: 간단예약관리 → 예약 타임테이블 */}
            <Route
              path="simple"
              element={<Navigate to="/appointments/timetable" replace />}
            />
            <Route path="timetable" element={<AppointmentTimetablePage />} />
            {/* V179: /appointments/products → /settings/reservation?tab=products 리다이렉트 */}
            <Route
              path="products"
              element={
                <Navigate to="/settings/reservation?tab=products" replace />
              }
            />
            <Route
              path="settings"
              element={
                <Navigate to="/settings/reservation?tab=settings" replace />
              }
            />
          </Route>

          {/* Settings Routes (Multi-page) */}
          {/* V1173: ORG_USER는 설정 접근 불가 → SettingsAccessRoute로 가드 */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsAccessRoute>
                  <DashboardLayout>
                    <Navigate to="/settings/general" replace />
                  </DashboardLayout>
                </SettingsAccessRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/general"
            element={
              <ProtectedRoute>
                <SettingsAccessRoute>
                  <DashboardLayout>
                    <GeneralSettingsPage />
                  </DashboardLayout>
                </SettingsAccessRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/operation"
            element={
              <ProtectedRoute>
                <SettingsAccessRoute>
                  <DashboardLayout>
                    <OperationSettingsPage />
                  </DashboardLayout>
                </SettingsAccessRoute>
              </ProtectedRoute>
            }
          />
          {/* /settings/services removed - merged into /appointments/products */}
          <Route
            path="/settings/reservation"
            element={
              <ProtectedRoute>
                <SettingsAccessRoute>
                  <DashboardLayout>
                    <ReservationSettingsPage />
                  </DashboardLayout>
                </SettingsAccessRoute>
              </ProtectedRoute>
            }
          />
          {/* V1080: /settings/customer-guide removed — KB(지식 관리)로 대체 */}
          {/* V198: Settings Change History Route */}
          {/* V1255 (RBAC policy revision): ORG_ADMIN+ 전용으로 격리 (직원 간 분란 방지 audit 거버넌스) */}
          <Route
            path="/settings/history"
            element={
              <ProtectedRoute>
                <OrgAdminRoute>
                  <DashboardLayout>
                    <SettingsChangeHistoryPage />
                  </DashboardLayout>
                </OrgAdminRoute>
              </ProtectedRoute>
            }
          />

          {/* V1044: AI Analysis Settings Route (SUPER_ADMIN, ADMIN only) */}
          <Route
            path="/settings/ai-analysis"
            element={
              <ProtectedRoute
                requiredRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}
              >
                <DashboardLayout>
                  <AIAnalysisSettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* V1153: Consultation Mode Settings Route */}
          <Route
            path="/settings/consultation-mode"
            element={
              <ProtectedRoute>
                <SettingsAccessRoute>
                  <DashboardLayout>
                    <ConsultationModePage />
                  </DashboardLayout>
                </SettingsAccessRoute>
              </ProtectedRoute>
            }
          />

          {/* Knowledge Base Route */}
          <Route
            path="/settings/knowledge-base"
            element={
              <ProtectedRoute>
                <SettingsAccessRoute>
                  <DashboardLayout>
                    <KnowledgeBasePage />
                  </DashboardLayout>
                </SettingsAccessRoute>
              </ProtectedRoute>
            }
          />

          {/* Subscription Route — V1173: ORG_ADMIN 전용 */}
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <OrgAdminRoute>
                  <DashboardLayout>
                    <SubscriptionPage />
                  </DashboardLayout>
                </OrgAdminRoute>
              </ProtectedRoute>
            }
          />

          {/* User Management Route — V1173: ORG_ADMIN 전용 */}
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <OrgAdminRoute>
                  <DashboardLayout>
                    <UserManagementPage />
                  </DashboardLayout>
                </OrgAdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Stats Route (ADMIN only) */}
          <Route
            path="/stats"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <StatsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Outbound Queue Route (POWER+ users) */}
          <Route
            path="/outbound-queue"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OutboundQueuePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Clinics Route (ADMIN only) */}
          <Route
            path="/admin/clinics"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AdminClinicsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Platform User Management Route (SUPER_ADMIN, ADMIN, OPERATOR only) */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PlatformUserManagementPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Announcements Route */}
          <Route
            path="/announcements"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AnnouncementsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* FAQ Route */}
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FaqPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Inquiry Routes */}
          <Route
            path="/inquiries"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <InquiriesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <InquiryDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Default Redirect - to Landing Page */}
          <Route path="/" element={<Navigate to="/main" replace />} />

          {/* 404 - Not Found */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundaryWithReset>
  );
}

/**
 * Main App Component
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="aicc-ui-theme">
        {/* V199: ModalStackProvider - 동적 모달 z-index 관리 */}
        <ModalStackProvider>
          <ClinicProvider>
            <NavigationGuardProvider>
              <Router>
                {/* 페이지 전환 시 스크롤 최상단 이동 */}
                <ScrollToTop />
                <AppContent />
                {/* V1104: Polling 기반 통화 알림 패널 (LiveCallPanel 대체) */}
                <CallNotificationPanel />
              </Router>
              <Toaster />
              {/* Sonner toast — Dialog/Sheet 위에 표시되도록 z-index 최상위 */}
              <SonnerToaster
                position="top-center"
                toastOptions={{
                  style: { zIndex: 99999 },
                }}
                richColors
              />
            </NavigationGuardProvider>
          </ClinicProvider>
        </ModalStackProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
